import { useEffect, useRef, useState } from "react";
import ContainerNavigationView from "../../common/view/ContainerNavigationView";
import styles from "../css/CSVImportView.module.css";
import { useTranslation } from "react-i18next";
import { ReactComponent as CloseIcon } from "../../../img/iconImgAllClose.svg";
import oneImage from "../../../img/csv_1.png";
import twoImage from "../../../img/csv_2.png";
import threeImage from "../../../img/csv_3.png";
import dialogWarning from "../../../img/dialogWarning.png";
import dialogSuccess from "../../../img/dialogCongratulations.png";
import SpacingView from "../../common/view/SpaingView";
import BigButton from "../../common/BigButton";
import { parseCsvFileAuto } from "../util/csvAuto";
import Term from "../../term/Term";
import { uploadTermList as uploadTermListFromAPI } from "../../term/termAPI";
import OnevocaNewDialog from "../../common/view/OnevocaNewDialog";
import { CSVTermListUploadMode } from "../model/CSVTermListUploadMode";
import { OnevocaNewDialogRadioButton } from "../../common/model/OnevocaNewDialogRadioButton";
import GroupSelectScreen from "../../groups/GroupSelectScreen";
import Group from "../../groups/Group";
import LoadingScreen from "../../common/LoadingScreen";
import { useBackToClose } from "../../../hooks/useBackToClose";

interface CSVImportViewProps {
  open: boolean;
  onSaveNewTerms: () => void;
  onClose: () => void;
}

export default function CSVImportView(props: CSVImportViewProps) {
  useBackToClose(props.open, props.onClose);
  const { t } = useTranslation();

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [termCount, setTermCount] = useState<number>(0);
  const [downloadedTermCount, setDownloadedTermCount] = useState<number>();

  const [loadedTerms, setLoadedTerms] = useState<Term[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string>();

  const [openDuplicatedDialog, setOpenDuplicatedDialog] =
    useState<boolean>(false);
  const [openDownloadSuccessDialog, setOpenDownloadSuccessDialog] =
    useState<boolean>(false);
  const [openGroupSelect, setOpenGroupSelect] = useState<boolean>(false);

  useEffect(() => {
    setSelectedGroup(undefined);
  }, [props.open]);

  // 사용자 클릭(그룹 선택) 흐름 안에서 직접 호출해야 브라우저가 파일 선택창을 막지 않음
  const handlePick = () => inputRef.current?.click();

  const handleFiles = async (files: FileList | null) => {
    if (!files || !files[0] || selectedGroup === undefined) return;

    try {
      const terms = await parseCsvFileAuto(files[0]);
      setLoadedTerms(terms);
      uploadTermList(
        terms,
        selectedGroup,
        CSVTermListUploadMode.checkDuplicate
      );
    } catch (e) {
      const msg = e instanceof Error ? e.message : "Unknown error";
      // Swift의 세 가지 케이스와 매칭:
      // - "Wrong csv file." (포맷/열기 실패)
      // - "This is wrong header foramt....([T, D, P, E])"
      // - "ErrorCSVFileNoData"
      alert(msg);
    } finally {
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  async function uploadTermList(
    terms: Term[],
    group: string,
    mode: CSVTermListUploadMode
  ) {
    try {
      setIsLoading(true);
      const result = await uploadTermListFromAPI(terms, group, mode);
      setIsLoading(false);
      if (result.isUploaded) {
        setDownloadedTermCount(result.uploadedCount);
        setOpenDownloadSuccessDialog(true);
        return;
      }

      setTermCount(result.duplicatedList.length);
      setOpenDuplicatedDialog(true);
    } catch (error) {
      setIsLoading(false);
      if (error instanceof Error) {
        alert(error.message);
      } else {
        alert(error);
      }
    }
  }

  function handleSelectedDuplicatesOption(id: number) {
    if (selectedGroup === undefined) return;
    let newMode: CSVTermListUploadMode | undefined;
    if (id === 1) {
      newMode = CSVTermListUploadMode.ignore;
    } else if (id === 2) {
      newMode = CSVTermListUploadMode.overrite;
    } else if (id === 3) {
      newMode = CSVTermListUploadMode.saveWithoutDuplicated;
    } else {
      newMode = undefined;
    }

    if (newMode === undefined) return;
    uploadTermList(loadedTerms, selectedGroup, newMode);
    setOpenDuplicatedDialog(false);
  }

  if (props.open === false) return null;

  return (
    <>
      <div className={styles.stack}>
        <div className={styles.container}>
          <div className={styles.contentView}>
            <div className={styles.csvImportContainer}>
              <ContainerNavigationView
                open={true}
                title={t("CSVImportMenu")}
                leftImageButtonImage={CloseIcon}
                onClickLeftImageButton={() => {
                  props.onClose();
                }}
              />

              <span className={styles.titleText}>
                {t("CSVImportTitleMessage")}
              </span>

              <SpacingView height={"16px"} />

              <span className={styles.sectionTitleText}>Step 1</span>
              <span className={styles.sectionMessageText}>
                {t("CSVExplainOne")}
              </span>
              <img className={styles.sectionImage} src={oneImage} alt="" />

              <span className={styles.sectionTitleText}>Step 2</span>
              <span className={styles.sectionMessageText}>
                {t("CSVExplainTwo")}
              </span>
              <img className={styles.sectionImage} src={twoImage} alt="" />

              <span className={styles.sectionTitleText}>Step 3</span>
              <span className={styles.sectionMessageText}>
                {t("CSVExplainThree")}
              </span>
              <img className={styles.sectionImage} src={threeImage} alt="" />

              <SpacingView height={"40px"} />

              <BigButton
                title={t("CSVLoadButton")}
                variant="normal"
                handleClick={() => {
                  setOpenGroupSelect(true);
                }}
              />
            </div>
          </div>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept=".csv,text/csv"
          style={{ display: "none" }}
          onChange={(e) => handleFiles(e.target.files)}
        />
        <GroupSelectScreen
          open={openGroupSelect}
          showAllGroup={false}
          showNoGroup={true}
          selectStyle={"single"}
          showGroupAddButton={true}
          handleOnSelectGroups={(groups: Group[]) => {
            if (groups.length === 0) return;
            const group = groups[0];
            setSelectedGroup(group.isUnspecifiedGroup ? "" : group.title);
            handlePick();
          }}
          handleOnClose={() => {
            setOpenGroupSelect(false);
          }}
        />
        <OnevocaNewDialog
          open={openDownloadSuccessDialog}
          title={t("CSVUploadSuccessMessage", { downloadedTermCount })}
          image={dialogSuccess}
          actionButtonTitle={t("Done")}
          canCancel={false}
          onDone={() => {
            setOpenDownloadSuccessDialog(false);
            props.onClose();
            props.onSaveNewTerms();
          }}
          onClose={() => {
            setOpenDownloadSuccessDialog(false);
          }}
        />
        <OnevocaNewDialog
          open={openDuplicatedDialog}
          title={t("CSVUploadDuplicatedListMessage", { termCount })}
          image={dialogWarning}
          actionButtonTitle={t("Done")}
          radioButtons={[
            new OnevocaNewDialogRadioButton(1, t("CSVUploadModeIgnore"), true),
            new OnevocaNewDialogRadioButton(
              2,
              t("CSVUploadModeOverwrite"),
              false
            ),
            new OnevocaNewDialogRadioButton(
              3,
              t("CSVUploadModeSaveWithoutDuplicates"),
              false
            ),
          ]}
          canCancel={true}
          onDoneWithRadioButton={handleSelectedDuplicatesOption}
          onClose={() => {
            setOpenDuplicatedDialog(false);
          }}
        />
        {isLoading === true && <LoadingScreen />}
      </div>
    </>
  );
}
